import { useEffect, useRef, useState } from "react"
import BaseMap, { BaseMapRef } from "./BaseMap"
import MetroLayers from "./layers/MetroLayers"
import MetroDrawLayer from "./layers/MetroDrawLayer"
import StateOutlineLayer from "./layers/StateOutlineLayer"
import { COUNTRY_VIEWS, isRealPosition, zoomForAddress } from "./mapUtils"
import { getActiveCountries, getCountry, useHub } from "../../core/Global"
import { API_ROUTES } from "../../core/Constants"
import { HereGeocodeItem, HereGeocodePosition } from "../../models/MapModels"
import JobMetro, { JobMetroLayer } from "../../modules/crm/base/models/JobMetro"

interface IProps {
    /** The metro being edited; none for a new one. */
    metro?: JobMetro
    countryId?: string
    stateId?: string
    /** An address searched for beside the map: the map goes to it, nothing else. */
    focus?: HereGeocodeItem
    color?: string
    height?: number | string
    onLayersChanged?: (layers: JobMetroLayer[]) => void
    onZoomChanged?: (zoom: number) => void
    onCenterChanged?: (center: HereGeocodePosition) => void
}

/**
 * The map for drawing a metro's areas: the state's border, the state's other metros faintly
 * for reference, and the metro's own circles, rectangles and polygons to draw, edit and delete.
 * It opens on the metro's areas, or on its state, or on its country; picking another state or
 * country takes it there.
 */
export default function MetroEditMap(props: IProps) {

    const hub = useHub()
    const mapRef = useRef<BaseMapRef>(null)
    const hasAreas = !!props.metro?.layers?.length

    // What the map opened with: a state or country picked later is gone to, the ones it opened
    // with are not -- the metro's own areas are the better view of an existing metro.
    const opened = useRef({ stateId: props.stateId, countryId: props.countryId })
    const fitState = !(hasAreas && props.stateId === opened.current.stateId)

    // The other metros of the state, for reference.
    const [others, othersSet] = useState<JobMetro[]>([])
    useEffect(() => {
        othersSet([])
        if (!props.stateId) return
        let q = "?stateId=" + props.stateId
        if (props.metro?.id) q += "&excludeId=" + props.metro.id
        hub.Get(API_ROUTES.JobMetrosAll + q, res => {
            if (res.success) othersSet((res.data ?? []).filter((m: JobMetro) => m.id !== props.metro?.id))
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.stateId])

    // Another country picked: the whole country. Opening on a country is only for a metro with
    // neither areas nor a state to open on.
    useEffect(() => {
        if (!props.countryId) return
        if (props.countryId === opened.current.countryId && (hasAreas || props.stateId)) return
        opened.current.countryId = undefined
        const country = getCountry(props.countryId) ?? getActiveCountries()?.find(c => c.id === props.countryId)
        const known = COUNTRY_VIEWS[country?.alpha2Code?.toLowerCase() ?? ""]
        if (known) mapRef.current?.getMap()?.setView([known.lat, known.lng], known.zoom)
        else if (country?.alpha3Code) hub.Get("/maps/geocode/country/" + country.alpha3Code, res => {
            if (res.success && res.data?.position) mapRef.current?.getMap()?.setView([res.data.position.lat, res.data.position.lng], 4)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.countryId])

    // A searched address: go there, as close as it is precise.
    useEffect(() => {
        const f = props.focus
        if (!f || !isRealPosition(f.position)) return
        mapRef.current?.getMap()?.setView([f.position.lat, f.position.lng], Math.max(10, zoomForAddress(f)))
    }, [props.focus])

    return (
        <BaseMap ref={mapRef} height={props.height}
            center={props.metro?.mapCenter} zoom={props.metro?.mapZoom ?? 4}
            onZoomChanged={props.onZoomChanged}
            onCenterChanged={props.onCenterChanged}>
            <StateOutlineLayer stateId={props.stateId} fit={fitState} />
            <MetroLayers metros={others} labels />
            <MetroDrawLayer layers={props.metro?.layers} color={props.color ?? props.metro?.color}
                fit={hasAreas} onChange={props.onLayersChanged} />
        </BaseMap>
    )
}
